import api from './api';

/**
 * Member Management Service
 * Handles all API calls for member CRUD operations
 */

const memberService = {
  /**
   * Get all members with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Number of records to return
   * @param {string} params.search - Search term for filtering
   * @param {string} params.status - Membership status filter
   * @returns {Promise} Response with members data
   */
  getMembers: async (params = {}) => {
    try {
      const response = await api.get('/api/v1/members', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching members:', error);
      throw error;
    }
  },

  /**
   * Lightweight member search for autocomplete use cases
   * @param {string} search - Query string (name, worship id, mobile, email)
   * @param {number} limit - Max results
   * @returns {Promise<Array>} Array of member records
   */
  searchMembers: async (search = '', limit = 10) => {
    try {
      const response = await api.get('/api/v1/members', {
        params: {
          search,
          limit,
          skip: 0
        }
      });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error searching members:', error);
      throw error;
    }
  },

  /**
   * Get a single member by ID
   * @param {number} memberId - Member ID
   * @returns {Promise} Member data
   */
  getMemberById: async (memberId) => {
    try {
      const response = await api.get(`/api/v1/members/${memberId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching member ${memberId}:`, error);
      throw error;
    }
  },

  /**
   * Search for a member by mobile number
   * @param {string} mobile - Mobile number
   * @returns {Promise} Member data
   */
  searchByMobile: async (mobile) => {
    try {
      const response = await api.get(`/api/v1/members/search/by-mobile/${mobile}`);
      return response.data;
    } catch (error) {
      console.error(`Error searching member by mobile ${mobile}:`, error);
      throw error;
    }
  },

  /**
   * Search for a member by worship number
   * @param {string} worshipNo - Worship number
   * @returns {Promise} Member data
   */
  searchByWorshipNo: async (worshipNo) => {
    try {
      const response = await api.get(`/api/v1/members/search/by-worship-no/${worshipNo}`);
      return response.data;
    } catch (error) {
      console.error(`Error searching member by worship number ${worshipNo}:`, error);
      throw error;
    }
  },

  /**
   * Suggest next available adult worship ID
   * @param {string} memberType - regular | associate | temporary
   * @returns {Promise<{next_worship_id: string, member_type: string}>}
   */
  getNextWorshipId: async (memberType = 'regular') => {
    const response = await api.get('/api/v1/members/next-worship-id', {
      params: { member_type: memberType }
    });
    return response.data;
  },

  /**
   * Create a new member
   * @param {Object} memberData - Member data
   * @returns {Promise} Created member data
   */
  createMember: async (memberData) => {
    try {
      const response = await api.post('/api/v1/members', memberData);
      return response.data;
    } catch (error) {
      console.error('Error creating member:', error);
      throw error;
    }
  },

  /**
   * Update an existing member
   * @param {number} memberId - Member ID
   * @param {Object} memberData - Updated member data
   * @returns {Promise} Updated member data
   */
  updateMember: async (memberId, memberData) => {
    try {
      const response = await api.put(`/api/v1/members/${memberId}`, memberData);
      return response.data;
    } catch (error) {
      console.error(`Error updating member ${memberId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a member
   * @param {number} memberId - Member ID
   * @returns {Promise} Delete confirmation
   */
  deleteMember: async (memberId) => {
    try {
      await api.delete(`/api/v1/members/${memberId}`);
      return { success: true };
    } catch (error) {
      console.error(`Error deleting member ${memberId}:`, error);
      throw error;
    }
  },

  /**
   * Get autocomplete suggestions for organization fields
   * @param {string} fieldType - Type of field (group, zone, district)
   * @param {string} search - Search term
   * @returns {Promise} List of matching values
   */
  getAutocomplete: async (fieldType, search = '') => {
    try {
      const response = await api.get(`/api/v1/members/autocomplete/${fieldType}`, { 
        params: { search } 
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${fieldType} autocomplete:`, error);
      throw error;
    }
  },

  /**
   * Get member statistics
   * @returns {Promise} Statistics data
   */
  getStatistics: async () => {
    try {
      const response = await api.get('/api/v1/members/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching member statistics:', error);
      throw error;
    }
  },

  /**
   * Get full decrypted HKID for a member
   * @param {number} memberId - Member ID
   * @returns {Promise} HKID data
   */
  getMemberHKID: async (memberId) => {
    try {
      const response = await api.get(`/api/v1/members/${memberId}/hkid`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching HKID for member ${memberId}:`, error);
      throw error;
    }
  }
};

export default memberService;
